





















































































































































import { Component, Mixins, Prop, Watch } from 'vue-property-decorator';

import Toast from '@/mixins/toast';
import ColumnTitleContent from '@/components/UI/Table/ColumnTitleContent.vue';

import MmHintedButton from '../../../components/MmHintedButton.vue';
import MmStates from '../../../components/MmStates.vue';
import MmTable from '../../../components/MmTable.vue';

import { fetchProducts } from '../../../api/catalog';
import { createCancelSource } from '../../../api/mmHttp';
import {
	buildPageQuery,
	createPaginationState,
	createSortingState,
	MM_QUERY_PARAM_NAMES,
	MmPaginationState,
	MmSortingState,
	resolvePagerTotal,
} from '../../../api/pagination';
import { formatDiagnostics, isCanceled, MmError, toMmError } from '../../../core/errors';
import { formatMoney, orDash } from '../../../core/format';
import {
	buildSelectionIndex,
	isRowSelected,
	MmSelectionIndex,
	MmSelectionState,
	resetSelectionArrays,
	selectedCount,
	toggleRow,
} from '../../../core/selection';
import { extractSkuKey, hasCompleteSkuKey, skuKeyOf } from '../../../core/skuKey';
import { MmProductRow, MmSkuKey } from '../../../types/domain';
import { MmTaskFilters } from '../../../types/dto';
import { MmErrorCode, MmTaskType } from '../../../types/enums';
import { canLoadProducts, getTaskTypeProfile, resolveProductsThresholdKey } from '../taskTypeProfile';

const KEY = 'priceMonitoring.mobileMonitoring.';

const SECTION_HINT = 'catalog/products';

/** check: 40 — колонка закреплена, ant рисует её отдельной таблицей и при 15px флажок обрежется */
const COLUMN_WIDTHS = {
	check: 40,
	format: 140,
	region: 140,
	cluster: 140,
	code: 160,
	product: 260,
	group: 150,
	category: 150,
	price: 110,
	salePoints: 130,
};

/** Описываем структурно: типы из .vue-модуля TypeScript импортировать не умеет */
interface MmTableChangePayload {
	pagination: MmPaginationState;
	sorter: MmSortingState;
	filters: { [key: string]: any };
}

/** Поле ввода ant шлёт это событие и при вводе, и при очистке крестиком */
interface MmInputChangeEvent {
	target: { value: string };
}

@Component({
	components: {
		ColumnTitleContent,
		MmHintedButton,
		MmStates,
		MmTable,
	},
})
export default class ProductsTableBlock extends Mixins(Toast) {
	@Prop({ default: null }) readonly tradingId!: number | null;

	/** Сырую модель формы сюда передавать нельзя: сентинел «Все» дал бы непустой фильтр */
	@Prop({ required: true }) readonly filters!: MmTaskFilters;

	@Prop({ required: true }) readonly taskType!: string;

	@Prop({ required: true }) readonly selection!: MmSelectionState<MmSkuKey>;

	@Prop({ default: false }) readonly disabled!: boolean;

	@Prop({ default: false }) readonly busy!: boolean;

	rows: MmProductRow[] = [];
	pagination: MmPaginationState = createPaginationState();
	sorting: MmSortingState = createSortingState();
	isLoading: boolean = false;
	loadError: MmError | null = null;

	searchQuery: string = '';
	appliedQuery: string = '';

	private pendingQuery: string | null = null;

	private listSource: any = null;

	mounted(): void {
		this.warnAboutTypeWithoutTable();
		this.loadProducts();
	}

	beforeDestroy(): void {
		this.cancelActiveRequest();
	}

	/** deep обязателен: форма мутирует массивы внутри объекта фильтров, а не подменяет объект */
	@Watch('filters', { deep: true })
	onFiltersChanged(): void {
		this.pagination.current = 1;
		this.loadProducts();
	}

	@Watch('isLoading')
	onLoadingFlagChanged(value: boolean): void {
		this.$emit('loading-change', value === true);
	}

	@Watch('tradingId')
	onTradingIdChanged(): void {
		this.pendingQuery = null;
		this.searchQuery = '';
		this.appliedQuery = '';
		this.pagination.current = 1;
		this.loadProducts();
	}

	/** Смена типа задания меняет саму выборку: бэкенд накладывает свой фильтр, поэтому запрос повторяем */
	@Watch('taskType')
	onTaskTypeChanged(): void {
		this.pagination.current = 1;
		this.loadProducts();
	}

	get selectionIndex(): MmSelectionIndex {
		return buildSelectionIndex(this.selection, skuKeyOf);
	}

	isChecked(row: MmProductRow): boolean {
		return isRowSelected(this.selection, this.selectionIndex, skuKeyOf(row));
	}

	handleRowChecked(row: MmProductRow, checked: boolean): void {
		toggleRow(this.selection, this.selectionIndex, extractSkuKey(row), skuKeyOf(row), checked, skuKeyOf);
		this.$emit('selection-changed');
	}

	handleCommonCheckboxChange(): void {
		resetSelectionArrays(this.selection);
		this.$emit('selection-changed');
	}

	get selectedProductsCount(): number {
		return selectedCount(this.selection, this.pagination.total);
	}

	get selectedText(): string {
		return this.$t(KEY + 'taskForm.selectedProducts', { count: this.selectedProductsCount }) as string;
	}

	get isSelectionDisabled(): boolean {
		return this.disabled || this.busy || this.isLoading;
	}

	get isSearchDisabled(): boolean {
		return this.disabled || this.busy;
	}

	get searchPending(): boolean {
		return this.searchQuery.trim() !== this.appliedQuery;
	}

	get searchDisabledReason(): string {
		if (!this.isSearchDisabled) {
			return '';
		}

		return this.busy ? (this.$t(KEY + 'taskForm.blockSubmitting') as string) : '';
	}

	get isSearchActive(): boolean {
		return this.appliedQuery !== '';
	}

	get isCommonCheckboxDisabled(): boolean {
		return this.isSelectionDisabled || this.isSearchActive;
	}

	get commonCheckboxDisabledReason(): string {
		return this.isSearchActive ? (this.$t(KEY + 'taskForm.selectAllDisabledSearch') as string) : '';
	}

	get selectAllAriaText(): string {
		return this.$t(KEY + 'taskForm.selectAllProductsAria') as string;
	}

	rowCheckboxAriaText(row: MmProductRow): string {
		const name = row && row.skuName ? String(row.skuName).trim() : '';
		const resolved = name !== '' ? name : (row && row.internalCode ? String(row.internalCode) : '');

		return this.$t(KEY + 'taskForm.selectProductAria', { name: resolved }) as string;
	}

	get columns(): any[] {
		return [
			{
				key: 'checkboxes',
				className: 'sm-mobilemon__products-check',
				scopedSlots: { title: 'commonCheckbox', customRender: 'rowCheckbox' },
				width: COLUMN_WIDTHS.check,
				/* fixed: 'left' работает только при заданном scroll.x и несовместим с expandedRowRender */
				fixed: 'left',
			},
			this.buildColumn('storeFormatName', 'colFormat', COLUMN_WIDTHS.format, 'formatCell', true),
			this.buildColumn('storeRegionName', 'colRegion', COLUMN_WIDTHS.region, 'regionCell', true),
			this.buildColumn('storeClusterName', 'colCluster', COLUMN_WIDTHS.cluster, 'clusterCell', true),
			this.buildColumn('internalCode', 'colInternalCode', COLUMN_WIDTHS.code, 'codeCell', true, true),
			this.buildColumn('skuName', 'colProduct', COLUMN_WIDTHS.product, 'productCell', true),
			this.buildColumn('groupName', 'colGroup', COLUMN_WIDTHS.group, 'groupCell', true),
			this.buildColumn('categoryName', 'colCategory', COLUMN_WIDTHS.category, 'categoryCell', true),
			this.buildColumn('regularPrice', 'colPrice', COLUMN_WIDTHS.price, 'priceCell', true),
			this.buildColumn('salePoints', 'colSalePoints', COLUMN_WIDTHS.salePoints, 'salePointsCell', false, true),
		];
	}

	get scrollX(): number {
		return COLUMN_WIDTHS.check
			+ COLUMN_WIDTHS.format
			+ COLUMN_WIDTHS.region
			+ COLUMN_WIDTHS.cluster
			+ COLUMN_WIDTHS.code
			+ COLUMN_WIDTHS.product
			+ COLUMN_WIDTHS.group
			+ COLUMN_WIDTHS.category
			+ COLUMN_WIDTHS.price
			+ COLUMN_WIDTHS.salePoints;
	}

	/** При длинном заголовке плоский title не ставим: он победил бы слот и шапка осталась бы пустой */
	private buildColumn(
		key: string,
		titleKey: string,
		width: number,
		slotName: string,
		sortable: boolean,
		longTitle: boolean = false
	): any {
		const column: any = {
			key: key,
			width: width,
			scopedSlots: { customRender: slotName },
		};

		if (longTitle) {
			column.slots = { title: key + 'ColumnTitle' };
			column.slotColumnTitleData = { text: this.$t(KEY + 'taskForm.' + titleKey) as string };
		} else {
			column.title = this.$t(KEY + 'taskForm.' + titleKey) as string;
		}

		if (sortable) {
			column.sorter = true;
			column.sortOrder = this.readSortOrder(key);
		}

		return column;
	}

	private readSortOrder(field: string): string | boolean {
		if (this.sorting.field !== field || this.sorting.order === null) {
			return false;
		}

		return this.sorting.order === 'desc' ? 'descend' : 'ascend';
	}

	buildRowKey(record: MmProductRow): string {
		return skuKeyOf(record);
	}

	readText(row: MmProductRow, field: string): string {
		const source: any = row;

		return orDash(source ? source[field] : null);
	}

	/** Ноль — настоящая цена и печатается как 0.00; прочерк только для отсутствующей */
	readPrice(row: MmProductRow): string {
		return formatMoney(row ? row.regularPrice : null);
	}

	readSalePointsCount(row: MmProductRow): string {
		const points = row && Array.isArray(row.salePoints) ? row.salePoints : null;

		return orDash(points === null ? null : points.length);
	}

	get canLoad(): boolean {
		return canLoadProducts(this.filters);
	}

	get tablePagination(): MmPaginationState | null {
		return this.pagination.total > 0 ? this.pagination : null;
	}

	get emptyStateName(): string {
		if (this.loadError === null) {
			return 'empty';
		}

		if (this.loadError.code === MmErrorCode.FORBIDDEN) {
			return 'forbidden';
		}

		return 'error';
	}

	get emptyStateTitle(): string {
		if (this.emptyStateName !== 'empty') {
			return '';
		}

		if (!this.canLoad) {
			const thresholdKey = resolveProductsThresholdKey(this.filters);

			return thresholdKey === null ? '' : (this.$t(thresholdKey) as string);
		}

		const profile = getTaskTypeProfile(this.taskType as MmTaskType);

		if (profile !== null && profile.productsEmptyKey !== '') {
			return this.$t(profile.productsEmptyKey) as string;
		}

		return this.$t(KEY + 'taskForm.productsEmpty') as string;
	}

	get emptyStateText(): string {
		if (this.emptyStateName === 'empty') {
			return '';
		}

		if (this.emptyStateName !== 'error') {
			return '';
		}

		const message = this.loadError !== null && this.loadError.message
			? String(this.loadError.message).trim()
			: '';

		return message !== '' ? message : (this.$t(KEY + 'taskForm.productsError') as string);
	}

	get isErrorRetryable(): boolean {
		return this.loadError !== null ? this.loadError.retryable : false;
	}

	get errorDiagnostics(): string {
		return this.loadError === null ? '' : formatDiagnostics(this.loadError.diagnostics);
	}

	handleRetry(): void {
		this.loadProducts();
	}

	fieldLabelId(field: string): string {
		return 'sm-mobilemon-products-table-' + field + '-label-' + String((this as any)._uid);
	}

	handleSearchInput(event: MmInputChangeEvent): void {
		const value = event && event.target ? event.target.value : '';

		this.searchQuery = value === null || value === undefined ? '' : String(value);
	}

	handleSearchSubmit(value: string): void {
		this.searchQuery = value === null || value === undefined ? '' : String(value);
		this.handleApplySearch();
	}

	handleApplySearch(): void {
		if (this.isLoading) {
			return;
		}

		this.applySearch();
	}

	private applySearch(): void {
		const next = this.searchQuery.trim();

		if (next === this.appliedQuery) {
			this.loadProducts();

			return;
		}

		/** Поиск снимает режим «выбрано всё»: решение принимает форма, до её ответа запрос не уходит */
		if (this.selection.isCommonCheckboxSelected) {
			this.pendingQuery = next;
			this.$emit('search-applied');

			return;
		}

		this.commitSearch(next);
	}

	private commitSearch(next: string): void {
		this.appliedQuery = next;
		this.pagination.current = 1;
		this.loadProducts();
	}

	resolvePendingSearch(confirmed: boolean): void {
		const next = this.pendingQuery;

		this.pendingQuery = null;

		if (next === null) {
			return;
		}

		if (!confirmed) {
			this.searchQuery = this.appliedQuery;

			return;
		}

		this.commitSearch(next);
	}

	handleTableChange(event: MmTableChangePayload): void {
		if (!event) {
			return;
		}

		this.pagination = event.pagination;
		this.sorting = event.sorter;
		this.loadProducts();
	}

	private loadProducts(): void {
		if (this.tradingId === null || !this.canLoad) {
			this.cancelActiveRequest();
			this.rows = [];
			this.loadError = null;
			this.pagination.total = 0;
			this.$emit('total-changed', 0);

			return;
		}

		this.cancelActiveRequest();

		const source = createCancelSource();

		this.listSource = source;
		this.isLoading = true;

		fetchProducts(
			this.tradingId,
			this.filters,
			this.buildPageParams(),
			{ cancelToken: source.token },
			this.taskType,
		)
			.then((page) => {
				if (this.listSource !== source) {
					return;
				}

				this.rows = page && page.content ? page.content : [];
				this.pagination.total = page ? resolvePagerTotal(page, this.pagination) : 0;
				this.loadError = null;
				this.warnAboutIncompleteKeys();
				this.$emit('total-changed', this.pagination.total);
			})
			.catch((error) => {
				if (isCanceled(error) || this.listSource !== source) {
					return;
				}

				this.rows = [];
				this.pagination.total = 0;
				this.loadError = toMmError(error, SECTION_HINT);
				this.$emit('total-changed', 0);
			})
			.finally(() => {
				if (this.listSource !== source) {
					return;
				}

				this.listSource = null;
				this.isLoading = false;
			});
	}

	/** Поиск уходит отдельным ключом: объект фильтров тем же составом уедет в создание задания */
	private buildPageParams(): { [key: string]: any } {
		const params = buildPageQuery(this.pagination, this.sorting);

		if (this.appliedQuery !== '') {
			params[MM_QUERY_PARAM_NAMES.search] = this.appliedQuery;
		}

		return params;
	}

	private cancelActiveRequest(): void {
		if (this.listSource !== null) {
			this.listSource.cancel();
			this.listSource = null;
		}

		this.isLoading = false;
	}

	private warnAboutIncompleteKeys(): void {
		if (process.env.NODE_ENV === 'production') {
			return;
		}

		let broken = 0;

		for (let i = 0; i < this.rows.length; i += 1) {
			if (!hasCompleteSkuKey(this.rows[i])) {
				broken += 1;
			}
		}

		if (broken === 0) {
			return;
		}

		// tslint:disable-next-line:no-console
		console.warn(
			'[ProductsTableBlock] строк без полного ключа MmSkuKey: ' + String(broken)
			+ '. Такие строки нельзя надёжно отметить и отправить в payload — '
			+ 'бэкенд обязан отдавать все семь полей идентификаторами.',
		);
	}

	private warnAboutTypeWithoutTable(): void {
		if (process.env.NODE_ENV === 'production') {
			return;
		}

		const profile = getTaskTypeProfile(this.taskType as MmTaskType);

		if (profile === null || profile.hasProductsTable) {
			return;
		}

		// tslint:disable-next-line:no-console
		console.warn(
			'[ProductsTableBlock] блок смонтирован для типа «' + String(this.taskType)
			+ '», у которого нет таблицы товаров: выбор не попадёт в payload. '
			+ 'Решение о рендере принимает форма по taskTypeProfile.hasProductsTable.',
		);
	}
}
